import {
  Column,
  DefectCheckinViewSchema,
  DefectCommentViewSchema,
  DefectMediaViewSchema,
  DefectViewSchema,
} from '@fsf/types';
import { _ } from '@ngx-translate/core';
import * as z from 'zod';

const DefectCommentSchema = DefectCommentViewSchema;
export type DefectComment = z.infer<typeof DefectCommentSchema>;

const DefectMediaSchema = DefectMediaViewSchema;
export type DefectMedia = z.infer<typeof DefectMediaSchema>;

const DefectCheckinSchema = DefectCheckinViewSchema;
export type DefectCheckin = z.infer<typeof DefectCheckinSchema>;

const DefectSchema = DefectViewSchema.extend({
  comments: z.array(DefectCommentSchema),
  media: z.array(DefectMediaSchema),
  checkins: z.array(DefectCheckinSchema),
  // FE-only flat projection of responsible_user?.name, populated by DefectService.
  // Enables PrimeNG sort/filter/global-search without nested-field handling.
  responsible_user_name: z.string().nullable().optional(),
});

export type Defect = z.infer<typeof DefectSchema>;

export type RouteWithSections = {
  id: number;
  name: string;
  sections: { id: string; name: string; type: string | null }[];
};

export type PsaOption = {
  id: string;
  name: string;
};

export type UserOption = {
  id: number;
  name: string;
};

const defectColumns: Column<keyof Defect>[] = [
  {
    header: 'defect.date',
    field: 'reporting_time',
    filterField: 'reporting_time',
    sortField: 'reporting_time',
    type: 'date',
    displayType: 'date',
    matchMode: 'between',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.title',
    field: 'title',
    filterField: 'title',
    sortField: 'title',
    type: 'text',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.type',
    field: 'model_type',
    filterField: 'model_type',
    sortField: 'model_type',
    type: 'text',
    textOptions: ['route_section', 'psa'],
    matchMode: 'in',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.modelName',
    field: 'model_name',
    filterField: 'model_name',
    sortField: 'model_name',
    type: 'text',
    matchMode: 'in',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.priority',
    field: 'priority',
    filterField: 'priority',
    sortField: 'priority',
    type: 'text',
    textOptions: ['low', 'medium', 'high', 'critical'],
    matchMode: 'in',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.responsibleUser',
    field: 'responsible_user_name',
    filterField: 'responsible_user_name',
    sortField: 'responsible_user_name',
    type: 'text',
    matchMode: 'in',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.dueDate',
    field: 'due_date',
    filterField: 'due_date',
    sortField: 'due_date',
    type: 'date',
    displayType: 'date',
    matchMode: 'between',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.estimatedRepairTime',
    field: 'estimated_repair_time',
    sortField: 'estimated_repair_time',
    type: 'numeric',
    showOperator: false,
    showMatchModes: false,
    showAddButton: false,
  },
  {
    header: 'defect.status',
    field: 'status',
    filterField: 'status',
    sortField: 'status',
    type: 'text',
    textOptions: ['new', 'in_progress', 'work_done', 'accepted'],
    matchMode: 'in',
    showOperator: false,
    showMatchModes: true,
    showAddButton: false,
  },
];

const globalFilterFields = ['title', 'description', 'model_name', 'responsible_user_name'];

export const DefectMetadata = {
  defectColumns,
  globalFilterFields,
};

export const defectTranslations = _([
  'defect.date',
  'defect.title',
  'defect.description',
  'defect.type',
  'defect.modelName',
  'defect.status',
  'defect.priority',
  'defect.priorities.low',
  'defect.priorities.medium',
  'defect.priorities.high',
  'defect.priorities.critical',
  'defect.dueDate',
  'defect.estimatedRepairTime',
  'defect.statuses.new',
  'defect.statuses.in_progress',
  'defect.statuses.work_done',
  'defect.statuses.accepted',
  'defect.types.route_section',
  'defect.types.psa',
  // Returned by the API as validation message (DefectController)
  'defect.commentRequiredOnRejection',
  'defect.repairTiming',
  'defect.repair_timing.during_hours',
  'defect.repair_timing.outside_hours',
  'defect.repair_timing.any',
  'defect.responsibleUser',
  'defect.notAssigned',
  'defect.checkins',
  'defect.checkinTime',
  'defect.checkoutTime',
  'defect.checkinUser',
]);

// Input schemas (→ StoreDefectRequest / UpdateDefectRequest)
export const StoreDefectSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().min(1),
  model_type: z.enum(['route_section', 'psa']),
  model_id: z.string().min(1).max(255),
  reporting_time: z.string().date(),
  status: z
    .enum(['new', 'in_progress', 'work_done', 'accepted'])
    .nullable()
    .optional(),
  priority: z
    .enum(['low', 'medium', 'high', 'critical'])
    .nullable()
    .optional(),
  due_date: z.string().date().nullable().optional(),
  // Geschätzte Reparaturzeit in Stunden
  estimated_repair_time: z.number().nullable().optional(),
  repair_timing: z
    .enum(['during_hours', 'outside_hours', 'any'])
    .nullable()
    .optional(),
  responsible_user_id: z.number().nullable().optional(),
});
export const UpdateDefectSchema = StoreDefectSchema.partial();
